function createDashboardItem(row, lineData) {
  return {
    id: 'db-' + row.id,
    text: row.inputText,
    pseudo: row.pseudo || '',
    lat: row.lat ?? null,
    lng: row.lng ?? null,
    data: lineData,
    script: row.script || 'Latn',
    knockoutSvg: row.knockoutSvg || null,
    target: row.target || null,
    canvas: null,
    ctx: null,
    bg: '#ffffff',
    fg: '#000000',
    tX: 0,
    rX: 0,
    ro: null,
    cardEl: null,
  };
}

export function createAppBootstrapFeature({
  fetchGenerations,
  loadFont,
  parseFont,
  prepareAnimLine,
  resolveLineData,
  getAnimFont,
  setAnimFont,
  dashboardItems,
  updateDashCount,
  onItemsLoaded,
  rebuildGalerie,
  renderAll,
  connectSSE,
  tabFromPath,
  switchTab,
  getPunchlineAnimAspectRatio,
  setPunchlineAnimAspectRatio,
  resizePunchlineAnimCanvas,
  punchlineAnimPreview,
  waitForAdminAccess,
  markDbLoaded,
}) {
  async function loadFromDB() {
    try {
      const rows = await fetchGenerations();
      if (!Array.isArray(rows) || rows.length === 0) return;

      const buf = await loadFont();
      if (!getAnimFont()) setAnimFont(await parseFont(buf));

      // Les cartes du dashboard ne sont plus construites ici (cf.
      // ensureDashboardCards dans app.js) : seulement les données partagées
      // par la galerie, la carte et le reste.
      const built = await Promise.all(rows.map(async (row) => {
        const text = row.inputText;
        if (!text) return null;
        const lineData = await resolveLineData({
          script: row.script, text, knockoutLineData: row.knockoutLineData,
          prepareAnimLine, font: getAnimFont(),
        });
        if (!lineData) return null;
        return createDashboardItem(row, lineData);
      }));

      for (const item of built) {
        if (item) dashboardItems.unshift(item);
      }

      updateDashCount();
      await new Promise(resolve => requestAnimationFrame(() => {
        onItemsLoaded?.();
        rebuildGalerie();
        resolve();
      }));
    } catch (error) {
      console.error('Erreur chargement DB:', error);
    } finally {
      // Distingue "en cours de chargement" de "confirmé vide" pour l'affichage
      // de la galerie (cf. tabNavigation/galerieFeature) : sans ça, le message
      // "Aucune punchline" s'affichait brièvement à chaque chargement de page,
      // avant même que la réponse du serveur soit arrivée.
      markDbLoaded?.();
    }
  }

  function revealMain() {
    document.querySelector('main').style.visibility = '';
  }

  function bindLoad() {
    document.addEventListener('DOMContentLoaded', async () => {
      // On affiche la coquille de page (header/nav/placeholders) tout de suite,
      // sans attendre la vérif admin ni les chargements réseau : le contenu
      // (galerie, carte, AR…) se remplit ensuite au fil de l'eau plutôt que de
      // laisser la page vide/figée plusieurs secondes le temps que tout arrive.
      const initialTab = tabFromPath();
      switchTab(initialTab, false);
      revealMain();

      // Ni le chargement des punchlines ni le calcul du ratio d'anim ne dépendent
      // de la vérif admin (ou l'un de l'autre) : on les lance tous en parallèle
      // dès le tout début plutôt qu'en série, pour que le contenu réel arrive
      // le plus tôt possible.
      const dbReady = loadFromDB();
      const aspectRatioReady = (async () => {
        if (getPunchlineAnimAspectRatio()) return;
        try {
          const buf = await loadFont();
          if (!getAnimFont()) setAnimFont(await parseFont(buf));
          const refLine = await prepareAnimLine('le moment EST venu', getAnimFont());
          if (refLine) {
            setPunchlineAnimAspectRatio(refLine.canvasH / refLine.pw);
            resizePunchlineAnimCanvas();
          }
        } catch {
          // noop
        }
      })();

      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => resizePunchlineAnimCanvas());
        ro.observe(punchlineAnimPreview.parentElement);
      }

      try {
        if (typeof waitForAdminAccess === 'function') {
          await waitForAdminAccess();
        }
        await renderAll();
        await Promise.all([dbReady, aspectRatioReady]);
        connectSSE();
        // Recalculé après waitForAdminAccess() : tabFromPath() renvoyait
        // 'galerie' pour /dashboard tant que le statut admin n'était pas
        // confirmé (canAccessDashboard() encore faux) -- réutiliser l'ancien
        // initialTab ici rouvrait toujours la galerie au lieu du dashboard,
        // y compris pour un admin déjà authentifié.
        const resolvedTab = tabFromPath();
        history.replaceState({ tab: resolvedTab }, '', window.location.pathname);
        switchTab(resolvedTab, false);
      } catch (err) {
        console.error('Bootstrap error:', err);
      }
    });
  }

  return {
    bindLoad,
    loadFromDB,
  };
}
